/* eslint-disable tailwindcss/no-arbitrary-value */

import { useEffect, useState } from 'react';
import {
  trackLeadCaptureClick,
  MembershipRewardsTrackingData,
} from 'utils/tracking';
import AddCheck from '$/components/icons/AddCheck';
import Alert from '$/components/icons/Alert';
import Like from '$/components/icons/Like';

type QuickFactsProps = {
  label: string;
  keyValue: {
    key: string;
    values: string[];
  }[];
  staticText: {
    dialogSeeMore: string;
    dialogSeeLess: string;
  };
  tracking: MembershipRewardsTrackingData;
};

export default function QuickFacts({
  label,
  keyValue,
  staticText,
  tracking,
}: QuickFactsProps): JSX.Element | null {
  const [isDesktop, setIsDesktop] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const updateMaxVisible = () => {
      setIsDesktop(window.innerWidth >= 1280);
    };

    updateMaxVisible();

    const resizeListener = () => updateMaxVisible();
    window.addEventListener('resize', resizeListener);
    return () => {
      window.removeEventListener('resize', updateMaxVisible);
    };
  }, []);

  const allValues = keyValue
    ?.flatMap((keyVal) =>
      keyVal?.values?.map((val) => ({ value: val, type: keyVal.key })),
    )
    .filter((item) => item?.value != null && item?.value !== '');

  const totalItems = allValues?.length ?? 0;
  const maxVisible = isDesktop ? totalItems : 3;
  const hiddenItems = totalItems - maxVisible;
  const showSeeMoreButton = totalItems > maxVisible;

  if (allValues.length === 0) {
    return null;
  }

  const visibleValues = allValues?.slice(
    0,
    isExpanded ? totalItems : maxVisible,
  );

  return (
    <div className="quick-facts flex flex-col items-start gap-[--spacing-gap-y-sm] self-stretch pt-[--spacing-gap-y-sm]">
      <div className="flex items-center justify-center gap-[10px] px-[--spacing-padding-y] py-0">
        <Like className="size-6" />
        <span className="mhg-alpha-subheading-2 text-center text-[--text-accent]">
          {label}
        </span>
      </div>
      <div className="mhg-alpha-box-shadow-1 flex flex-col items-start self-stretch rounded-b-[--radius-card] rounded-t-none border-r border-solid border-r-[--borders-default] bg-[--fill-card]">
        <div
          className={`flex items-start self-stretch border-t-2 border-solid border-t-[--fill-primary] pb-[--spacing-padding-y-xs] ${showSeeMoreButton ? 'lg:pb-0' : 'lg:pb-4'}`}
        >
          <div className="flex flex-[1_0_0] flex-col items-start gap-[--spacing-gap-y-sm] px-[--spacing-padding-x-sm] pb-0 pt-[--spacing-padding-y] lg:px-[--spacing-padding-x]">
            {visibleValues?.map((item, index) => (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                className="flex items-start gap-[--spacing-gap-x-xs] self-stretch"
              >
                <div className="mt-[2px] flex">
                  {item?.type === 'pros' ? (
                    <AddCheck className="size-[--shapes-icon-size-md] text-[--shapes-icon-size-md]" />
                  ) : (
                    <Alert className="size-[--shapes-icon-size-md] text-[--shapes-icon-size-md]" />
                  )}
                </div>
                <div
                  className="mhg-alpha-body-2-relaxed text-[--text-primary]"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: item?.value ?? '',
                  }}
                ></div>
              </div>
            ))}
          </div>
        </div>
        {showSeeMoreButton && (
          <button
            type="button"
            className="mhg-alpha-button-2 mhg-alpha-spacing-padding-y-spacing-padding-x flex w-full flex-col items-start justify-center font-[--font-weight-button-1] text-[--text-link]"
            onClick={() => {
              setIsExpanded(!isExpanded);
              trackLeadCaptureClick(
                'accordion',
                `${label}_${!isExpanded ? 'See More' : 'See Less'}`,
                tracking,
                'lead-submission',
                1,
                'product_interaction',
              );
            }}
          >
            {isExpanded
              ? staticText.dialogSeeLess
              : `${staticText.dialogSeeMore} (${hiddenItems})`}
          </button>
        )}
      </div>
    </div>
  );
}
